import React from 'react';
import { useSupabaseData } from '../../hooks/useSupabaseData';
import { leadsService, projectsService, templatesService } from '../../services/supabase';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ExportButton from '../../components/ui/ExportButton';
import { 
  Calendar,
  Bell,
  FileText,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Target
} from 'lucide-react';

const Organization: React.FC = () => {
  const { t } = useI18n();
  const { showToast } = useToast();
  // Avoid full-table hydration on this page: we only need leads/projects for counts.
  const { leads, projects } = useSupabaseData({
    include: { leads: true, projects: true, categories: false },
  });

  const handleExport = async () => {
    try {
      // Fetch ALL data directly from services to ensure we get everything
      const [allLeadsFromService, allProjectsFromService, allTemplatesFromService] = await Promise.all([
        leadsService.getAll(),
        projectsService.getAll(),
        templatesService.getAll()
      ]);
      
      // Prepare leads data for export
      const leadsData = allLeadsFromService.map(lead => ({
      [t('common.type')]: t('leads.singular'),
      [t('leads.name')]: lead.name,
      [t('leads.contactValue')]: lead.contact_value || '',
      [t('leads.contactType')]: lead.contact_type || '',
      [t('leads.status')]: lead.status,
      [t('leads.sector')]: lead.sector_id || '',
      [t('leads.notes')]: lead.notes || '',
      [t('leads.createdAt')]: lead.createdAt?.toISOString().split('T')[0] || ''
    }));

      // Prepare projects data for export
      const projectsData = allProjectsFromService.map(project => {
        const lead = allLeadsFromService.find(l => l.id === project.leadId);
      return {
        [t('common.type')]: t('projects.singular'),
        [t('projects.name')]: project.name,
        [t('projects.lead')]: lead?.name || t('projects.unknownLead'),
        [t('projects.deliverableType')]: project.type,
        [t('projects.status')]: project.status,
        [t('projects.amount')]: project.amount,
        [t('projects.currency')]: project.currency,
        [t('projects.deadline')]: project.deadline?.toISOString().split('T')[0] || '',
        [t('projects.brief')]: project.brief || '',
        [t('projects.createdAt')]: project.createdAt?.toISOString().split('T')[0] || ''
      };
    });

      // Prepare templates data for export
      const templatesData = allTemplatesFromService.map(template => ({
      [t('common.type')]: t('templates.singular'),
      [t('templates.name')]: template.name,
      [t('templates.contentType')]: template.type,
      [t('templates.subject')]: template.subject || '',
      [t('templates.content')]: template.content || '',
      [t('templates.createdAt')]: template.createdAt?.toISOString().split('T')[0] || ''
    }));

      const exportData = [...leadsData, ...projectsData, ...templatesData];

      const csvContent = [
        Object.keys(exportData[0] || {}).join(','),
        ...exportData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `organisation-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting organization data:', error);
      showToast(t('organization.exportError') || 'Erreur lors de l\'export des données d\'organisation', 'error');
    }
  };

  // Mock data for now since we don't have organization-specific data
  const mockReminders = 0;
  const mockActivities = 0;

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('organization.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('organization.manageOrganization')}
          </p>
        </div>
        <div className="hidden sm:block">
          <ExportButton onExport={handleExport} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
        <Card padding="md" className="text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-3">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{leads.length}</div>
          <div className="text-sm text-gray-600">{t('organization.totalLeads')}</div>
        </Card>
        
        <Card padding="md" className="text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-3">
            <FileText className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{projects.length}</div>
          <div className="text-sm text-gray-600">{t('organization.totalProjects')}</div>
        </Card>
        
        <Card padding="md" className="text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-3">
            <Bell className="w-6 h-6 text-yellow-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{mockReminders}</div>
          <div className="text-sm text-gray-600">{t('organization.reminders')}</div>
        </Card>
        
        <Card padding="md" className="text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mx-auto mb-3">
            <Clock className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{mockActivities}</div>
          <div className="text-sm text-gray-600">{t('organization.recentActivities')}</div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>{t('organization.schedule')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-3">📅</div>
              <p className="text-gray-500 mb-4">{t('organization.scheduleDescription')}</p>
              <Button variant="outline" size="sm">
                {t('organization.viewCalendar')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="w-5 h-5" />
              <span>{t('organization.goals')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-3">🎯</div>
              <p className="text-gray-500 mb-4">{t('organization.goalsDescription')}</p>
              <Button variant="outline" size="sm">
                {t('organization.setGoals')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5" />
              <span>{t('organization.tasks')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-3">✅</div>
              <p className="text-gray-500 mb-4">{t('organization.tasksDescription')}</p>
              <Button variant="outline" size="sm">
                {t('organization.viewTasks')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5" />
            <span>{t('organization.recentActivity')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Activité récente</h3>
            <p className="text-gray-500">Les activités seront disponibles quand cette fonctionnalité sera implémentée</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Organization;